/**
 * Frontend Logger Utility
 * Comprehensive logging for debugging and error tracking
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  component?: string;
  action?: string;
  userId?: string;
  [key: string]: unknown;
}

// CSS styles for console output
const styles = {
  debug: 'color: #6B7280; font-weight: normal;',
  info: 'color: #3B82F6; font-weight: bold;',
  warn: 'color: #F59E0B; font-weight: bold;',
  error: 'color: #EF4444; font-weight: bold;',
  api: 'color: #8B5CF6; font-weight: bold;',
  component: 'color: #10B981; font-weight: bold;',
  hook: 'color: #EC4899; font-weight: bold;',
  context: 'color: #6B7280; font-style: italic;',
};

class Logger {
  private isDev: boolean;
  private logLevel: LogLevel;

  constructor() {
    this.isDev = import.meta.env.DEV;
    this.logLevel = (import.meta.env.VITE_LOG_LEVEL as LogLevel) || 'debug';
  }

  private shouldLog(level: LogLevel): boolean {
    const levels: LogLevel[] = ['debug', 'info', 'warn', 'error'];
    return levels.indexOf(level) >= levels.indexOf(this.logLevel);
  }

  private formatContext(context?: LogContext): string {
    if (!context || Object.keys(context).length === 0) return '';
    return Object.entries(context)
      .filter(([, v]) => v !== undefined)
      .map(([k, v]) => `${k}=${JSON.stringify(v)}`)
      .join(' ');
  }

  debug(message: string, context?: LogContext): void {
    if (!this.isDev || !this.shouldLog('debug')) return;
    console.log(
      `%c[DEBUG]%c ${message} %c${this.formatContext(context)}`,
      styles.debug,
      '',
      styles.context
    );
  }

  info(message: string, context?: LogContext): void {
    if (!this.shouldLog('info')) return;
    console.log(
      `%c[INFO]%c ${message} %c${this.formatContext(context)}`,
      styles.info,
      '',
      styles.context
    );
  }

  warn(message: string, context?: LogContext): void {
    if (!this.shouldLog('warn')) return;
    console.warn(
      `%c[WARN]%c ${message} %c${this.formatContext(context)}`,
      styles.warn,
      '',
      styles.context
    );
  }

  error(message: string, error?: unknown, context?: LogContext): void {
    if (!this.shouldLog('error')) return;
    console.error(
      `%c[ERROR]%c ${message} %c${this.formatContext(context)}`,
      styles.error,
      '',
      styles.context
    );
    if (error) {
      console.error(error);
    }
  }

  // API call logging
  api(method: string, url: string, status?: number, duration?: number, context?: LogContext): void {
    if (!this.isDev) return;
    
    const statusColor = !status ? styles.api
      : status >= 500 ? styles.error
      : status >= 400 ? styles.warn
      : styles.info;

    console.log(
      `%c[API]%c ${method} ${url} %c${status || 'pending'}%c ${duration ? `${duration}ms` : ''} %c${this.formatContext(context)}`,
      styles.api,
      '',
      statusColor,
      styles.context,
      styles.context
    );
  }

  // Component lifecycle logging
  component(name: string, event: 'mount' | 'unmount' | 'render' | 'update', context?: LogContext): void {
    if (!this.isDev) return;
    console.log(
      `%c[COMPONENT]%c ${name} %c${event}%c %c${this.formatContext(context)}`,
      styles.component,
      '',
      styles.debug,
      '',
      styles.context
    );
  }

  // Hook logging
  hook(name: string, action: string, context?: LogContext): void {
    if (!this.isDev) return;
    console.log(
      `%c[HOOK]%c ${name} %c${action}%c %c${this.formatContext(context)}`,
      styles.hook,
      '',
      styles.debug,
      '',
      styles.context
    );
  }

  // User action logging
  action(action: string, context?: LogContext): void {
    if (!this.isDev) return;
    console.log(
      `%c[ACTION]%c ${action} %c${this.formatContext(context)}`,
      styles.info,
      '',
      styles.context
    );
  }

  // Performance logging
  perf(label: string, duration: number, context?: LogContext): void {
    if (!this.isDev) return;
    const color = duration > 1000 ? styles.warn : duration > 100 ? styles.info : styles.debug;
    console.log(
      `%c[PERF]%c ${label} %c${duration}ms%c %c${this.formatContext(context)}`,
      color,
      '',
      color,
      '',
      styles.context
    );
  }

  // Group related logs
  group(label: string, fn: () => void): void {
    if (!this.isDev) {
      fn();
      return;
    }
    console.group(label);
    fn();
    console.groupEnd();
  }

  // Table logging for arrays/objects
  table(data: unknown[], columns?: string[]): void {
    if (!this.isDev) return;
    if (columns) {
      console.table(data, columns);
    } else {
      console.table(data);
    }
  }
}

export const logger = new Logger();
